import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  FlightTakeoff as FlightTakeoffIcon,
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import WorkflowStatusCard from '../common/WorkflowStatusCard';
import WorkflowGuide from '../common/WorkflowGuide';
import QuickAuditTrail from '../common/QuickAuditTrail';
import RemarksHistory from '../common/RemarksHistory';
import SectorTabsView from './SectorTabsView';
import TripOverview from './TripOverview';
import TripsTable from './TripsTable';
import TripsMonthCalendar from '../common/TripsMonthCalendar';
import PanelExpandButton, { getCharterCalendarPaperSx, calendarEnlargeDialogPaperSx } from '../common/PanelExpandButton';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  createDefaultSectorForm
} from './SalesEnquiryUtils';
import { utcToDisplayDateAndTime, displayDateTimeToUTC } from '../../utils/timezoneUtils';
import TripAndSectorDialogs from './SalesEnquiry-part2';
import OtherDialogs, { canApproveAM, canApproveCAMO, canEditTrip, canCancelTrip } from './SalesEnquiry-part3';

const MAX_REPLICATE_DATES = 15;

const SalesEnquiry = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tripDialogOpen, setTripDialogOpen] = useState(false);
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [editingSector, setEditingSector] = useState(null);
  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [selectedTripForRemarks, setSelectedTripForRemarks] = useState(null);
  const [remarksForm, setRemarksForm] = useState({ remarks: '' });
  const [remarksError, setRemarksError] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState(null);
  const [itineraryDialogOpen, setItineraryDialogOpen] = useState(false);
  const [selectedTripForItinerary, setSelectedTripForItinerary] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedTripForApproval, setSelectedTripForApproval] = useState(null);
  const [approvalType, setApprovalType] = useState(null); // 'AM' or 'CA_MANAGER'
  const [approvalForm, setApprovalForm] = useState({ decision: 'APPROVED', remarks: '' });
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedTripForCancel, setSelectedTripForCancel] = useState(null);
  const [cancelForm, setCancelForm] = useState({ cancellationReason: '', description: '' });
  const [cancelError, setCancelError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Replicate charter onto multiple calendar days
  const [replicateMode, setReplicateMode] = useState(false);
  const [replicateSourceTrip, setReplicateSourceTrip] = useState(null);
  const [replicateSelectedDates, setReplicateSelectedDates] = useState([]);
  const [replicateConfirmOpen, setReplicateConfirmOpen] = useState(false);
  const [replicateLoading, setReplicateLoading] = useState(false);
  const [cloneReviewOpen, setCloneReviewOpen] = useState(false);
  const [createdClones, setCreatedClones] = useState([]);
  const [expandedPanel, setExpandedPanel] = useState(null); // null | 'table' | 'calendar'

  // Trip-level tabs
  const [tripDetailTab, setTripDetailTab] = useState(0);

  // Client settings (timezones for display/storage)
  const [clientSettings, setClientSettings] = useState({
    storageTimezone: 'UTC',
    displayTimezone: 'Asia/Kolkata'
  });

  // Check if user can create trips (SALES, OPERATIONS, ACCOUNTABLE_MANAGER, SUPER_ADMIN)
  const canCreateTrips = user?.role === ROLES.SALES || user?.role === ROLES.OPERATIONS_MANAGER || user?.role === ROLES.OPERATIONS_EXECUTIVE || user?.role === ROLES.ACCOUNTABLE_MANAGER || user?.role === ROLES.SUPER_ADMIN;
  const isSalesUser = user?.role === ROLES.SALES;
  const isAccountableManager = user?.role === ROLES.ACCOUNTABLE_MANAGER || user?.role === ROLES.SUPER_ADMIN;
  const isCAMO = user?.role === ROLES.CA_MANAGER || user?.role === ROLES.SUPER_ADMIN;

  // Trip form state
  const [tripForm, setTripForm] = useState({
    title: '',
    flightType: 'CHARTER',
    clientName: '',
    clientContact: { email: '', phone: '' },
    passengers: [],
    totalWeight: 0,
    baggageWeight: 20,
    specialRequirements: '',
    itineraryDisplayStatus: '',
    itineraryJourneyNotes: '',
    newRemark: '',
    salesTemplateId: 'default',
    requiresBA: true,
    requiresATCClearance: true
  });

  // Sector form state
  const [sectorForm, setSectorForm] = useState(() => createDefaultSectorForm());

  const amFocus = searchParams.get('amFocus');
  const opsFocus = searchParams.get('opsFocus');
  const tripsForTable = useMemo(() => {
    if (amFocus === 'charter_am') {
      return trips.filter(
        (trip) =>
          trip?.status === 'CREW_ASSIGNMENT' &&
          trip?.approvals?.accountableManager?.approved !== true
      );
    }
    if (amFocus === 'charter_camo') {
      return trips.filter(
        (trip) =>
          trip?.status === 'CREW_ASSIGNMENT' &&
          trip?.approvals?.accountableManager?.approved === true &&
          trip?.approvals?.camo?.approved !== true
      );
    }
    if (opsFocus === 'draft') {
      return trips.filter((trip) => trip?.status === 'DRAFT');
    }
    if (opsFocus === 'pending_ops') {
      return trips.filter((trip) => trip?.status === 'PENDING_OPS');
    }
    if (opsFocus === 'crew_assignment') {
      return trips.filter((trip) => trip?.status === 'CREW_ASSIGNMENT');
    }
    return trips;
  }, [trips, amFocus, opsFocus]);

  const focusAlertMessage = useMemo(() => {
    if (amFocus === 'charter_am') return 'Showing only charters pending AM approval.';
    if (amFocus === 'charter_camo') return 'Showing only charters pending CAMO approval (after AM approval).';
    if (opsFocus === 'draft') return 'Showing only draft charters from the Head of Operations dashboard.';
    if (opsFocus === 'pending_ops') return 'Showing only charters pending Operations (PENDING_OPS).';
    if (opsFocus === 'crew_assignment') return 'Showing only charters in CREW_ASSIGNMENT.';
    return null;
  }, [amFocus, opsFocus]);

  const clearFocusFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('amFocus');
    next.delete('opsFocus');
    setSearchParams(next);
  };

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      const response = await axios.get('/api/sales/trips', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        console.error('Authentication failed. Please log in again.');
      } else {
        console.error('Failed to fetch trips:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAircraft = useCallback(async () => {
    try {
      // SALES users don't need to fetch all aircraft - they only see what's assigned to sectors
      // Aircraft info is already populated in trip details
      if (user?.role === ROLES.SALES) {
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) {
        return; // No token, skip fetch
      }
      const response = await axios.get('/api/aircraft', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAircraft(response.data);
    } catch (error) {
      // Only log error if it's not a 401 (unauthorized) - this is expected for SALES users or expired tokens
      if (error.response?.status !== 401) {
        console.error('Failed to fetch aircraft:', error);
      }
    }
  }, [user]);

  const fetchPilots = useCallback(async () => {
    try {
      if (user?.role === ROLES.SALES) {
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get('/api/ops/crew-duty-roster/crew-list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPilots(response.data || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Failed to fetch pilots:', error);
      }
    }
  }, [user]);

  const fetchTripDetail = async (tripId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/sales/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTrip(response.data.trip);
      setSectors(response.data.sectors);
    } catch (error) {
      console.error('Failed to fetch trip detail:', error);
    }
  };

  const openRemarksDialog = async (trip) => {
    setSelectedTripForRemarks(trip);
    setRemarksForm({ remarks: '' });
    setRemarksError(null);
    
    // Fetch sectors for this trip to validate before allowing send to operations
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/sales/trips/${trip._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tripSectors = response.data.sectors || [];
      
      // Check if SALES role is trying to send without sectors
      if (user?.role === ROLES.SALES && tripSectors.length === 0) {
        setRemarksError('Cannot send charter to Operations: At least one sector must be created before sending to Operations. Please add sectors to this charter first.');
        setRemarksDialogOpen(true);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch trip sectors:', error);
      // Continue anyway - backend will validate
    }
    
    setRemarksDialogOpen(true);
  };

  const openAuditTrail = (trip) => {
    setAuditTrailData({
      entityType: 'charter',
      entityId: trip.charterNumber,
      entityName: `${trip.charterNumber} - ${trip.title}`
    });
    setAuditTrailOpen(true);
  };

  const openItineraryDialog = (trip) => {
    setSelectedTripForItinerary(trip);
    setItineraryDialogOpen(true);
  };

  const openApprovalDialog = (trip, type) => {
    setSelectedTripForApproval(trip);
    setApprovalType(type);
    setApprovalForm({ decision: 'APPROVED', remarks: '' });
    setApprovalDialogOpen(true);
  };


  const openCancelDialog = (trip) => {
    if (!canCancelTrip(trip, user)) {
      showToast('Completed charters cannot be cancelled.', 'warning');
      return;
    }
    setSelectedTripForCancel(trip);
    setCancelForm({ cancellationReason: '', description: '' });
    setCancelError(null);
    setCancelDialogOpen(true);
  };

  const cancelReplicateMode = () => {
    setReplicateMode(false);
    setReplicateSourceTrip(null);
    setReplicateSelectedDates([]);
    setReplicateConfirmOpen(false);
  };

  const startReplicate = async (trip) => {
    if (!trip?._id || !canCreateTrips) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/sales/trips/${trip._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tripSectors = response.data.sectors || [];
      if (tripSectors.length === 0) {
        showToast('Add at least one sector before replicating this charter.', 'error');
        return;
      }
      setSelectedTrip(response.data.trip);
      setSectors(tripSectors);
      setReplicateSourceTrip(response.data.trip);
      setReplicateSelectedDates([]);
      setReplicateMode(true);
      showToast('Select days on the calendar, then create DRAFT clones.', 'info');
    } catch (error) {
      console.error('Failed to start replicate:', error);
      showToast(error.response?.data?.message || 'Failed to load charter for replicate', 'error');
    }
  };

  const toggleReplicateDate = (dateKey) => {
    setReplicateSelectedDates((prev) => {
      if (prev.includes(dateKey)) {
        return prev.filter((d) => d !== dateKey);
      }
      if (prev.length >= MAX_REPLICATE_DATES) {
        showToast(`You can select at most ${MAX_REPLICATE_DATES} days.`, 'warning');
        return prev;
      }
      return [...prev, dateKey].sort();
    });
  };

  const openReplicateConfirm = () => {
    if (!replicateSourceTrip || replicateSelectedDates.length === 0) {
      showToast('Select at least one day on the calendar.', 'warning');
      return;
    }
    setReplicateConfirmOpen(true);
  };

  const submitReplicate = async () => {
    if (!replicateSourceTrip?._id || replicateSelectedDates.length === 0) return;
    setReplicateLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/sales/trips/${replicateSourceTrip._id}/replicate`,
        { dates: replicateSelectedDates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const clones = response.data.clones || [];
      await fetchTrips();
      cancelReplicateMode();
      setCreatedClones(clones);
      setCloneReviewOpen(true);
      if (clones[0]?._id) {
        await fetchTripDetail(clones[0]._id);
      }
      showToast(
        `Created ${clones.length} DRAFT charter${clones.length === 1 ? '' : 's'}. Review and edit each before sending to Ops.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to replicate charter:', error);
      showToast(error.response?.data?.message || 'Failed to replicate charter', 'error');
    } finally {
      setReplicateLoading(false);
    }
  };

  const openCloneFromReview = async (clone) => {
    if (!clone?._id) return;
    await fetchTripDetail(clone._id);
  };

  const resetTripForm = () => {
    setTripForm({
      title: '',
      flightType: 'CHARTER',
      clientName: '',
      clientContact: { email: '', phone: '' },
      passengers: [],
      totalWeight: 0,
      baggageWeight: 20,
      specialRequirements: '',
      itineraryDisplayStatus: '',
      itineraryJourneyNotes: '',
      newRemark: '',
      salesTemplateId: 'default',
      requiresBA: true,
      requiresATCClearance: true
    });
  };

  const resetSectorForm = () => {
    setSectorForm(createDefaultSectorForm(sectors.length + 1, clientSettings.displayTimezone));
  };

  const openTripDialog = (trip = null) => {
    if (trip) {
      setEditingTrip(trip);
      const passengers = trip.passengers || [];
      const passengerSum = passengers.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);
      const baggageWeight = trip.baggageWeight != null ? trip.baggageWeight : 20;
      setTripForm({
        title: trip.title,
        flightType: trip.flightType || 'CHARTER',
        clientName: trip.clientName || '',
        clientContact: trip.clientContact || { email: '', phone: '' },
        passengers,
        totalWeight: passengerSum + baggageWeight,
        baggageWeight,
        specialRequirements: trip.specialRequirements || '',
        itineraryDisplayStatus: trip.itineraryDisplayStatus || '',
        itineraryJourneyNotes: trip.itineraryJourneyNotes || '',
        newRemark: '',
        salesTemplateId: trip.salesTemplateId || 'default',
        requiresBA: true,
        requiresATCClearance: true
      });
    } else {
      setEditingTrip(null);
      resetTripForm();
    }
    setTripDialogOpen(true);
  };

  const handlePassengerUpdate = (passengers, passengerTotalWeight) => {
    setTripForm(prev => {
      const baggage = prev.baggageWeight != null ? prev.baggageWeight : 20;
      return {
        ...prev,
        passengers,
        totalWeight: passengerTotalWeight + baggage
      };
    });
  };

  const openSectorDialog = () => {
    setEditingSector(null);
    const initialForm = createDefaultSectorForm(sectors.length + 1, clientSettings.displayTimezone);
    if (sectors.length >= 1) {
      const lastSector = sectors[sectors.length - 1];
      const prevArrivalAirport = lastSector.arrival?.airport || '';
      initialForm.departure = { ...initialForm.departure, airport: prevArrivalAirport };
    }
    setSectorForm(initialForm);
    setSectorDialogOpen(true);
  };

  const openEditSectorDialog = (sector) => {
    if (!sector) return;
    const dep = sector.departure || {};
    const arr = sector.arrival || {};
    const displayTz = clientSettings.displayTimezone || 'Asia/Kolkata';
    const depDt = dep.date ? utcToDisplayDateAndTime(dep.date, displayTz) : { date: '', time: '' };
    const arrDt = arr.estimatedDate ? utcToDisplayDateAndTime(arr.estimatedDate, displayTz) : { date: '', time: '' };
    setSectorForm({
      seqNo: sector.seqNo ?? 1,
      departure: {
        airport: dep.airport || '',
        date: depDt.date,
        time: depDt.time,
        fullName: dep.fullName || '',
        coordinates: dep.coordinates || '',
        facilityType: dep.facilityType || ''
      },
      arrival: {
        airport: arr.airport || '',
        estimatedDate: arrDt.date,
        estimatedTime: arrDt.time,
        fullName: arr.fullName || '',
        coordinates: arr.coordinates || '',
        facilityType: arr.facilityType || ''
      },
      wakeTurbulence: sector.wakeTurbulence || '',
      flightRules: sector.flightRules || '',
      flightType: sector.flightType || 'CHARTER',
      charterServiceLabel: sector.charterServiceLabel || '',
      itineraryNotes: sector.itineraryNotes || '',
      groundTransferNotes: sector.groundTransferNotes || '',
      departureGroundHandler: {
        contactPerson: sector.departureGroundHandler?.contactPerson || '',
        contactNumber: sector.departureGroundHandler?.contactNumber || ''
      },
      arrivalGroundHandler: {
        contactPerson: sector.arrivalGroundHandler?.contactPerson || '',
        contactNumber: sector.arrivalGroundHandler?.contactNumber || ''
      },
      departureTransport: {
        vehicleDetails: sector.departureTransport?.vehicleDetails || ''
      },
      arrivalTransport: {
        vehicleDetails: sector.arrivalTransport?.vehicleDetails || ''
      },
      remarks: ''
    });
    setEditingSector(sector);
    setSectorDialogOpen(true);
  };

  const updateSector = async () => {
    if (!selectedTrip || !editingSector) return;
    const displayTz = clientSettings.displayTimezone || 'Asia/Kolkata';
    try {
      const token = localStorage.getItem('token');
      const departureDateISO = sectorForm.departure?.date
        ? (displayDateTimeToUTC(
            sectorForm.departure.date.trim(),
            sectorForm.departure?.time || '00:00',
            displayTz
          ) ?? undefined)
        : undefined;
      const arrivalDateISO = sectorForm.arrival?.estimatedDate
        ? (displayDateTimeToUTC(
            sectorForm.arrival.estimatedDate.trim(),
            sectorForm.arrival?.estimatedTime || '00:00',
            displayTz
          ) ?? undefined)
        : undefined;
      const payload = {
        seqNo: sectorForm.seqNo ?? 1,
        departure: {
          airport: sectorForm.departure?.airport || '',
          date: departureDateISO,
          time: sectorForm.departure?.time || '',
          fullName: sectorForm.departure?.fullName || undefined,
          coordinates: sectorForm.departure?.coordinates || undefined,
          facilityType: sectorForm.departure?.facilityType || undefined
        },
        arrival: {
          airport: sectorForm.arrival?.airport || '',
          estimatedDate: arrivalDateISO,
          estimatedTime: sectorForm.arrival?.estimatedTime || undefined,
          fullName: sectorForm.arrival?.fullName || undefined,
          coordinates: sectorForm.arrival?.coordinates || undefined,
          facilityType: sectorForm.arrival?.facilityType || undefined
        },
        wakeTurbulence: sectorForm.wakeTurbulence || '',
        flightRules: sectorForm.flightRules || '',
        flightType: sectorForm.flightType || 'CHARTER',
        charterServiceLabel: sectorForm.charterServiceLabel || '',
        itineraryNotes: sectorForm.itineraryNotes || '',
        groundTransferNotes: sectorForm.groundTransferNotes || '',
        departureGroundHandler: sectorForm.departureGroundHandler || undefined,
        arrivalGroundHandler: sectorForm.arrivalGroundHandler || undefined,
        departureTransport: sectorForm.departureTransport || undefined,
        arrivalTransport: sectorForm.arrivalTransport || undefined
      };
      if (sectorForm.remarks != null && sectorForm.remarks.trim()) payload.remarks = sectorForm.remarks.trim();
      await axios.put(`/api/sales/trips/${selectedTrip._id}/sectors/${editingSector._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      setSectorDialogOpen(false);
      setEditingSector(null);
      resetSectorForm();
      showToast(`Sector ${editingSector.seqNo} updated successfully.`, 'success');
    } catch (error) {
      console.error('Failed to update sector:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to update sector';
      showToast(msg, 'error');
    }
  };

  const cancelSector = async (sector) => {
    if (!selectedTrip || !sector) return;
    const isCompleted = ['COMPLETED', 'SECTOR_COMPLETED'].includes(sector.status)
      || ['COMPLETED', 'CHARTER_COMPLETED'].includes(selectedTrip.status);
    const confirmMsg = isCompleted
      ? 'Cancel this completed sector? Charter status will be recalculated from remaining sectors.'
      : 'Cancel this sector? If it is the only sector, the charter will be cancelled.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/sales/trips/${selectedTrip._id}/sectors/${sector._id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      showToast(`Sector ${sector.seqNo} cancelled.`, 'success');
    } catch (error) {
      console.error('Failed to cancel sector:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to cancel sector';
      showToast(msg, 'error');
    }
  };

  const emergencyGroundSector = async (sector) => {
    if (!selectedTrip || !sector) return;
    if (!window.confirm(`Emergency ground charter from sector ${sector.seqNo}? This will cancel this and all remaining sectors and set flight status to GROUNDED.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/sales/trips/${selectedTrip._id}/sectors/${sector._id}/emergency-grounded`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      showToast(`Charter emergency grounded from sector ${sector.seqNo}.`, 'success');
    } catch (error) {
      console.error('Failed to emergency ground charter:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to emergency ground charter';
      showToast(msg, 'error');
    }
  };

  useEffect(() => {
    fetchTrips();
    fetchAircraft();
    fetchPilots();
  }, [fetchTrips, fetchAircraft, fetchPilots]);

  useEffect(() => {
    const loadClientSettings = async () => {
      try {
        const response = await axios.get('/api/client/settings');
        setClientSettings({
          storageTimezone: response.data.storageTimezone ?? response.data.timezone ?? 'UTC',
          displayTimezone: response.data.displayTimezone ?? 'Asia/Kolkata'
        });
      } catch {
        // keep defaults
      }
    };
    loadClientSettings();
  }, []);

  // Reset trip detail tab when a new trip is selected
  useEffect(() => {
    setTripDetailTab(0);
  }, [selectedTrip?._id]);

  // Handle navigation state from notifications
  useEffect(() => {
    if (location.state) {
      const { selectedTripId, selectedSectorId, charterNumber } = location.state;
      
      // If we have a trip ID or charter number, select that trip
      if (selectedTripId) {
        // Find trip by ID
        const findAndSelectTrip = async () => {
          try {
            await fetchTripDetail(selectedTripId);
            // If we have a specific sector ID, scroll to it or highlight it
            if (selectedSectorId) {
              // Sector selection can be handled by scrolling or highlighting
              setTimeout(() => {
                const sectorElement = document.querySelector(`[data-sector-id="${selectedSectorId}"]`);
                if (sectorElement) {
                  sectorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  sectorElement.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
                  setTimeout(() => {
                    sectorElement.style.backgroundColor = '';
                  }, 2000);
                }
              }, 500);
            }
          } catch (error) {
            console.error('Failed to fetch trip from navigation state:', error);
            // Try to find by charter number if ID fails
            if (charterNumber) {
              const trip = trips.find(t => t.charterNumber === charterNumber);
              if (trip) {
                await fetchTripDetail(trip._id);
              }
            }
          }
        };
        
        // Wait for trips to load first
        if (trips.length > 0) {
          findAndSelectTrip();
        } else {
          // If trips not loaded yet, wait a bit and try again
          const timer = setTimeout(() => {
            findAndSelectTrip();
          }, 500);
          return () => clearTimeout(timer);
        }
      } else if (charterNumber) {
        // Find trip by charter number
        const findAndSelectTrip = async () => {
          const trip = trips.find(t => t.charterNumber === charterNumber);
          if (trip) {
            await fetchTripDetail(trip._id);
          }
        };
        
        if (trips.length > 0) {
          findAndSelectTrip();
        } else {
          const timer = setTimeout(() => {
            findAndSelectTrip();
          }, 500);
          return () => clearTimeout(timer);
        }
      }
      
      // Clear navigation state after processing
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, trips, navigate]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FlightTakeoffIcon color="success" fontSize="large" />
        Flight Entry Management
      </Typography>
      <WorkflowGuide variant="sales" />

      <Grid container spacing={3}>
        {/* Charter List + Calendar — full width for responsive desktop/Mac layout */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {focusAlertMessage ? (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={clearFocusFilters}>
                      Show all
                    </Button>
                  }
                >
                  {focusAlertMessage}
                </Alert>
              ) : null}
              {replicateMode && replicateSourceTrip && (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        disabled={replicateSelectedDates.length === 0 || replicateLoading}
                        onClick={openReplicateConfirm}
                      >
                        Create {replicateSelectedDates.length || ''} draft{replicateSelectedDates.length === 1 ? '' : 's'}
                      </Button>
                      <Button color="inherit" size="small" onClick={cancelReplicateMode}>
                        Cancel
                      </Button>
                    </Stack>
                  }
                >
                  Replicating <strong>{replicateSourceTrip.charterNumber || replicateSourceTrip.title}</strong>
                  {' — '}select days on the calendar ({replicateSelectedDates.length}/{MAX_REPLICATE_DATES}).
                  Clones stay DRAFT so you can edit them before sending to Ops.
                </Alert>
              )}
              {!replicateMode && createdClones.length > 0 && !cloneReviewOpen && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => setCloneReviewOpen(true)}>
                      Open Clone Review
                    </Button>
                  }
                >
                  {createdClones.length} DRAFT clone{createdClones.length === 1 ? '' : 's'} ready to edit and send to Ops.
                </Alert>
              )}
              <Paper
                elevation={0}
                sx={{
                  ...getCharterCalendarPaperSx(expandedPanel),
                  borderColor: replicateMode ? 'secondary.main' : 'grey.700'
                }}
              >
                {expandedPanel !== 'calendar' && (
                <Box
                  sx={{
                    position: 'relative',
                    minWidth: 0,
                    minHeight: expandedPanel === 'table'
                      ? { xs: '65vh', md: '70vh' }
                      : { xs: 420, md: 0 },
                    overflow: 'hidden',
                    border: '3px solid',
                    borderColor: 'grey.700',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box
                    sx={{
                      position: {
                        xs: 'relative',
                        md: expandedPanel === 'table' ? 'relative' : 'absolute'
                      },
                      inset: expandedPanel === 'table' ? undefined : { md: 0 },
                      height: {
                        xs: '100%',
                        md: expandedPanel === 'table' ? '70vh' : 'auto'
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      p: 1.5,
                      overflow: 'hidden',
                      minHeight: expandedPanel === 'table'
                        ? { xs: '65vh', md: '70vh' }
                        : { xs: 420, md: 0 }
                    }}
                  >
                    <TripsTable
                      trips={tripsForTable}
                      selectedTrip={selectedTrip}
                      onTripClick={fetchTripDetail}
                      onEditClick={openTripDialog}
                      onRemarksClick={openRemarksDialog}
                      onAuditTrailClick={openAuditTrail}
                      onItineraryClick={openItineraryDialog}
                      onApprovalClick={openApprovalDialog}
                      onCancelClick={openCancelDialog}
                      onReplicateClick={startReplicate}
                      user={user}
                      isAccountableManager={isAccountableManager}
                      aircraft={aircraft}
                      canCreateTrips={canCreateTrips}
                      onNewTripClick={() => openTripDialog()}
                      fillHeight
                      expandControl={(
                        <PanelExpandButton
                          expandedPanel={expandedPanel}
                          panel="table"
                          onChange={setExpandedPanel}
                        />
                      )}
                    />
                  </Box>
                </Box>
                )}
                {expandedPanel !== 'table' && (
                <Box
                  sx={{
                    minWidth: 0,
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1.5,
                    border: '3px solid',
                    borderColor: 'grey.700',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      flexShrink: 0
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Trip Calendar
                    </Typography>
                    <PanelExpandButton
                      expandedPanel={expandedPanel}
                      panel="calendar"
                      onChange={setExpandedPanel}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <TripsMonthCalendar
                      trips={tripsForTable}
                      selectedTrip={selectedTrip}
                      onTripClick={fetchTripDetail}
                      aircraft={aircraft}
                      pilots={pilots}
                      embedded
                      selectionMode={replicateMode}
                      selectedDates={replicateSelectedDates}
                      onToggleDate={toggleReplicateDate}
                      maxSelected={MAX_REPLICATE_DATES}
                      highlightTripIds={createdClones.map((c) => c._id)}
                    />
                  </Box>
                </Box>
                )}
              </Paper>

              <Dialog
                open={expandedPanel === 'calendar'}
                onClose={() => setExpandedPanel(null)}
                maxWidth={false}
                PaperProps={{ sx: calendarEnlargeDialogPaperSx }}
              >
                <DialogTitle
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.25,
                    flexShrink: 0
                  }}
                >
                  <Typography variant="h6" component="span">Trip Calendar</Typography>
                  <PanelExpandButton
                    expandedPanel={expandedPanel}
                    panel="calendar"
                    onChange={setExpandedPanel}
                  />
                </DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <TripsMonthCalendar
                    trips={tripsForTable}
                    selectedTrip={selectedTrip}
                    onTripClick={(tripId) => {
                      fetchTripDetail(tripId);
                      setExpandedPanel(null);
                    }}
                    aircraft={aircraft}
                    pilots={pilots}
                    embedded
                    enlarged
                    selectionMode={replicateMode}
                    selectedDates={replicateSelectedDates}
                    onToggleDate={toggleReplicateDate}
                    maxSelected={MAX_REPLICATE_DATES}
                    highlightTripIds={createdClones.map((c) => c._id)}
                  />
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </Grid>

        {/* Sectors List — full width below */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Sectors {selectedTrip && `- ${selectedTrip.title}`}
                </Typography>
                {selectedTrip && (() => {
                  // Completed charters can still receive additional sectors; cancelled/grounded stay locked
                  const canAddSector = !['CANCELLED', 'GROUNDED'].includes(selectedTrip.status);
                  return canAddSector && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openSectorDialog}
                    >
                      Add Sector
                    </Button>
                  );
                })()}
              </Box>

              {selectedTrip ? (
                <>
                  <TripOverview selectedTrip={selectedTrip} sectors={sectors} />
                  
                  {/* Trip Detail Tabs */}
                  {selectedTrip && (
                    <Box sx={{ mb: 2 }}>
                      <Tabs
                        value={tripDetailTab}
                        onChange={(e, newValue) => setTripDetailTab(newValue)}
                        sx={{ mb: 2 }}
                      >
                        <Tab label="Workflow Status" />
                        <Tab label="Remarks" />
                        <Tab label="Recent Activity" />
                      </Tabs>

                      {tripDetailTab === 0 && (
                        <WorkflowStatusCard trip={selectedTrip} user={user} />
                      )}

                      {tripDetailTab === 1 && (
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Remarks History
                            </Typography>
                            {selectedTrip.remarksHistory && selectedTrip.remarksHistory.length > 0 ? (
                              <RemarksHistory remarksHistory={selectedTrip.remarksHistory} entityType="trip" />
                            ) : (
                              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                No remarks yet
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {tripDetailTab === 2 && (
                        <QuickAuditTrail
                          entityType="charter"
                          entityId={selectedTrip.charterNumber || selectedTrip._id}
                          entityName={selectedTrip.charterNumber || selectedTrip.title}
                          maxItems={10}
                        />
                      )}
                    </Box>
                  )}

                  {/* Sector Tabs View */}
                  <SectorTabsView 
                    sectors={sectors} 
                    selectedTrip={selectedTrip} 
                    aircraft={aircraft}
                    user={user}
                    displayTimezone={clientSettings.displayTimezone}
                    onEditSector={openEditSectorDialog}
                    onCancelSector={cancelSector}
                    onEmergencyGroundSector={emergencyGroundSector}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a charter to view sectors
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Trip and Sector Dialogs */}
      <TripAndSectorDialogs
        tripDialogOpen={tripDialogOpen}
        setTripDialogOpen={setTripDialogOpen}
        sectorDialogOpen={sectorDialogOpen}
        setSectorDialogOpen={setSectorDialogOpen}
        editingTrip={editingTrip}
        setEditingTrip={setEditingTrip}
        editingSector={editingSector}
        setEditingSector={setEditingSector}
        selectedTrip={selectedTrip}
        sectors={sectors}
        setTrips={setTrips}
        setSelectedTrip={setSelectedTrip}
        setSectors={setSectors}
        fetchTrips={fetchTrips}
        fetchTripDetail={fetchTripDetail}
        tripForm={tripForm}
        setTripForm={setTripForm}
        sectorForm={sectorForm}
        setSectorForm={setSectorForm}
        resetTripForm={resetTripForm}
        resetSectorForm={resetSectorForm}
        handlePassengerUpdate={handlePassengerUpdate}
        updateSector={updateSector}
        displayTimezone={clientSettings.displayTimezone}
      />

      {/* Other Dialogs */}
      <OtherDialogs
        remarksDialogOpen={remarksDialogOpen}
        setRemarksDialogOpen={setRemarksDialogOpen}
        auditTrailOpen={auditTrailOpen}
        setAuditTrailOpen={setAuditTrailOpen}
        itineraryDialogOpen={itineraryDialogOpen}
        setItineraryDialogOpen={setItineraryDialogOpen}
        approvalDialogOpen={approvalDialogOpen}
        setApprovalDialogOpen={setApprovalDialogOpen}
        cancelDialogOpen={cancelDialogOpen}
        setCancelDialogOpen={setCancelDialogOpen}
        selectedTrip={selectedTrip}
        setTrips={setTrips}
        setSelectedTrip={setSelectedTrip}
        fetchTrips={fetchTrips}
        fetchTripDetail={fetchTripDetail}
        selectedTripForRemarks={selectedTripForRemarks}
        setSelectedTripForRemarks={setSelectedTripForRemarks}
        selectedTripForItinerary={selectedTripForItinerary}
        setSelectedTripForItinerary={setSelectedTripForItinerary}
        selectedTripForApproval={selectedTripForApproval}
        setSelectedTripForApproval={setSelectedTripForApproval}
        selectedTripForCancel={selectedTripForCancel}
        setSelectedTripForCancel={setSelectedTripForCancel}
        auditTrailData={auditTrailData}
        setAuditTrailData={setAuditTrailData}
        remarksForm={remarksForm}
        setRemarksForm={setRemarksForm}
        remarksError={remarksError}
        setRemarksError={setRemarksError}
        sendLoading={sendLoading}
        setSendLoading={setSendLoading}
        approvalType={approvalType}
        setApprovalType={setApprovalType}
        approvalForm={approvalForm}
        setApprovalForm={setApprovalForm}
        approvalLoading={approvalLoading}
        setApprovalLoading={setApprovalLoading}
        cancelForm={cancelForm}
        setCancelForm={setCancelForm}
        cancelError={cancelError}
        setCancelError={setCancelError}
        cancelLoading={cancelLoading}
        setCancelLoading={setCancelLoading}
      />

      <Dialog
        open={replicateConfirmOpen}
        onClose={() => !replicateLoading && setReplicateConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Create {replicateSelectedDates.length} DRAFT charter
          {replicateSelectedDates.length === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            From <strong>{replicateSourceTrip?.charterNumber || replicateSourceTrip?.title}</strong>.
            Each selected day gets its own DRAFT clone with the same clock times. You can edit clones before sending to Ops.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {replicateSelectedDates.map((d) => (
              <Chip
                key={d}
                size="small"
                label={dayjs(d).format('DD MMM YYYY')}
                color="secondary"
                variant="outlined"
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplicateConfirmOpen(false)} disabled={replicateLoading}>
            Back
          </Button>
          <Button onClick={submitReplicate} variant="contained" disabled={replicateLoading}>
            {replicateLoading ? 'Creating…' : 'Create drafts'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={cloneReviewOpen}
        onClose={() => setCloneReviewOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Clone Review</Typography>
          <Button size="small" onClick={() => setCloneReviewOpen(false)}>
            Close
          </Button>
        </Box>
        <Divider />
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
          Open each DRAFT to edit trip or sectors, then send to Ops when ready.
        </Typography>
        <List dense sx={{ px: 1 }}>
          {createdClones.map((clone) => {
            const isActive = selectedTrip && String(selectedTrip._id) === String(clone._id);
            const dateLabel = clone.listFlightDate
              ? dayjs(clone.listFlightDate).format('DD MMM YYYY')
              : clone.targetDate
                ? dayjs(clone.targetDate).format('DD MMM YYYY')
                : '—';
            return (
              <ListItemButton
                key={clone._id}
                selected={isActive}
                onClick={() => openCloneFromReview(clone)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={clone.charterNumber || clone.title}
                  secondary={`${dateLabel} · ${clone.status || 'DRAFT'}${clone.title ? ` · ${clone.title}` : ''}`}
                  primaryTypographyProps={{ fontWeight: isActive ? 700 : 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItemButton>
            );
          })}
          {createdClones.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
              No clones in this session.
            </Typography>
          )}
        </List>
        {selectedTrip && createdClones.some((c) => String(c._id) === String(selectedTrip._id)) && (
          <Box sx={{ p: 2, mt: 'auto' }}>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => openTripDialog(selectedTrip)}
              >
                Edit Trip
              </Button>
              {selectedTrip.status === 'DRAFT' && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => openRemarksDialog(selectedTrip)}
                >
                  Send to Ops
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default SalesEnquiry;
